import { Dispatch, SetStateAction } from 'react';
import {
  CustomPresetState,
  COMP_PER_TYPES,
  DATE_PRESETS,
  RangeErrorPreset,
  defaultIsDayOrRangeInvalid,
  computeDateFromPreset
} from '@blisspointmedia/bpm-types/dist/RelativeDatePresets';
import { CompanyFiscalCal } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import cn from 'classnames';
import { Button } from '../../Button';
import { Appearance, Design } from '../../designConstants';
import { DateRange } from '../date-picker-types';
import { CustomDatePresets } from '../CustomDatePresets';
import './DPPresets.scss';

interface DPPresetsProps {
  selectedPreset:
    ((typeof DATE_PRESETS)[number][number] | (typeof COMP_PER_TYPES)[number][number]) | null;
  setSelectedPreset: (preset: string) => void;
  setDateInputs: Dispatch<SetStateAction<Partial<DateRange>>>;
  resolvedIsOutsideValidRange?: (date: string) => boolean;
  startCustomPreset: CustomPresetState;
  setStartCustomPreset: Dispatch<SetStateAction<CustomPresetState>>;
  endCustomPreset: CustomPresetState;
  setEndCustomPreset: Dispatch<SetStateAction<CustomPresetState>>;
  rangeErrorPreset: RangeErrorPreset;
  setRangeErrorPreset: Dispatch<SetStateAction<RangeErrorPreset>>;
  comparison?: boolean;
  fiscalCal?: CompanyFiscalCal;
  fiscalCalOverride?: CompanyFiscalCal;
}

export const DPPresets = ({
  selectedPreset,
  setSelectedPreset,
  setDateInputs,
  resolvedIsOutsideValidRange = defaultIsDayOrRangeInvalid,
  startCustomPreset,
  setStartCustomPreset,
  endCustomPreset,
  setEndCustomPreset,
  rangeErrorPreset,
  setRangeErrorPreset,
  fiscalCal,
  fiscalCalOverride,
  comparison = false
}: DPPresetsProps) => {
  const buttonOptions = comparison ? COMP_PER_TYPES : DATE_PRESETS;

  return (
    <div className='dp-presets'>
      <div className='standard-presets'>
        {buttonOptions.map((group, i) => (
          <div className='date-preset-options-container' key={`DPPresetOptionContainer${i}`}>
            {group.map((preset, j) => {
              let isDisabled = false;
              if (fiscalCal) {
                const dateRange = computeDateFromPreset(preset, fiscalCal);
                const startDate = dateRange?.start || '';
                const endDate = dateRange?.end || '';
                isDisabled =
                  resolvedIsOutsideValidRange(startDate) && resolvedIsOutsideValidRange(endDate);
              }
              return (
                <Button
                  key={`DPPresetOptionButton${i}${j}`}
                  appearance={Appearance.EMPTY}
                  design={Design.NEUTRAL}
                  onClick={() => {
                    setSelectedPreset(preset);
                  }}
                  className={cn('control-button', {
                    selected: selectedPreset === preset,
                    'date-preset-button': !comparison
                  })}
                  disabled={isDisabled}
                >
                  {preset}
                </Button>
              );
            })}
          </div>
        ))}
      </div>
      <div className='custom-presets'>
        <Button
          key='DPPresetOptionButtonPrimaryCustom'
          appearance={Appearance.EMPTY}
          design={Design.NEUTRAL}
          onClick={() => {
            setSelectedPreset('Custom');
          }}
          className={cn('control-button', 'date-preset-button', {
            selected: selectedPreset === 'Custom'
          })}
        >
          Custom
        </Button>
        <CustomDatePresets
          setRangeError={setRangeErrorPreset}
          setDateInputs={setDateInputs}
          invalidOrOutOfRange={resolvedIsOutsideValidRange}
          startCustomPreset={startCustomPreset}
          setStartCustomPreset={setStartCustomPreset}
          endCustomPreset={endCustomPreset}
          setEndCustomPreset={setEndCustomPreset}
          disabled={selectedPreset !== 'Custom'}
          rangeError={rangeErrorPreset}
          fiscalCalOverride={fiscalCalOverride}
        />
      </div>
    </div>
  );
};
